/**
 * RedSeguridadGlobal — la red que impide que una clienta vea la pantalla en blanco.
 *
 * ENCARGO Ann (2026-07-16): "monta la red de seguridad global, cada día más seguros".
 * Motivo: la plataforma NO tenía ErrorBoundary global ni <Suspense>. Cualquier fallo
 * en cualquier punto tiraba el árbol de React entero y dejaba la página EN BLANCO:
 * sin mensaje, sin explicación y sin salida. La clienta concluía —con razón— que la
 * plataforma estaba rota, y nos llamaba (o se iba). Ann: "los avisos no les dan
 * alternativas" · "ayuda a la gente a entender qué pasa".
 *
 * Qué hace:
 *  1) Atrapa CUALQUIER error de render de sus hijos → nunca más blanco.
 *  2) Explica en cristiano qué ha pasado y que NO es culpa suya.
 *  3) Da SALIDAS: reintentar, recargar, ir al inicio.
 *  4) Nos chiva el fallo (fire-and-forget) para poder arreglarlo de verdad.
 *
 * Decisiones de robustez (importantes: esto es lo último que queda en pie):
 *  - Componente de CLASE sin hooks: los ErrorBoundary solo funcionan así.
 *  - CERO dependencias frágiles: no usa i18n (si lo que falló es i18n, el propio
 *    aviso caería), ni router, ni librerías de UI. Diccionario ES/CA interno y
 *    navegación por window.location. Estilos en línea.
 *  - Sin emojis: icono SVG, paleta corporativa, letra grande (gente mayor).
 *  - El chivatazo va en try/catch: si falla, no rompe el aviso.
 */
import React from 'react';

const TEXTOS = {
  es: {
    titulo: 'Algo no ha ido bien',
    tranquila: 'No es culpa tuya y no has perdido nada. Esta pantalla no se ha podido cargar.',
    consejo: 'Prueba a volver a intentarlo. Si sigue igual, vuelve al inicio y entra otra vez.',
    reintentar: 'Volver a intentarlo',
    recargar: 'Recargar la página',
    inicio: 'Ir al inicio',
    ayuda: 'Si vuelve a pasarte, avísanos y lo miramos: es un fallo nuestro, no tuyo.',
    detalles: 'Detalles técnicos',
  },
  ca: {
    titulo: 'Alguna cosa no ha anat bé',
    tranquila: 'No és culpa teva i no has perdut res. Aquesta pantalla no s’ha pogut carregar.',
    consejo: 'Prova de tornar-ho a intentar. Si continua igual, torna a l’inici i entra un altre cop.',
    reintentar: 'Tornar-ho a intentar',
    recargar: 'Recarregar la pàgina',
    inicio: 'Anar a l’inici',
    ayuda: 'Si et torna a passar, avisa’ns i ho mirem: és una errada nostra, no teva.',
    detalles: 'Detalls tècnics',
  },
};

const _idioma = () => {
  try {
    const l = (document.documentElement.lang || localStorage.getItem('i18nextLng') || 'es').toLowerCase();
    return l.startsWith('ca') ? 'ca' : 'es';
  } catch (e) {
    return 'es';
  }
};

const S = {
  wrap: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '32px 18px', boxSizing: 'border-box',
    background: 'radial-gradient(130% 70% at 50% 0%,#E1F4EF 0%,#EAF6F4 55%)',
    fontFamily: "'Montserrat', system-ui, -apple-system, Arial, sans-serif",
  },
  card: {
    background: '#fff', borderRadius: 22, boxShadow: '0 18px 50px rgba(44,107,118,.16)',
    width: '100%', maxWidth: 480, padding: '32px 28px 28px', boxSizing: 'border-box',
    textAlign: 'center',
  },
  titulo: { margin: '0 0 10px', fontSize: 25, fontWeight: 800, color: '#16323a', lineHeight: 1.25 },
  texto: { margin: '0 0 10px', fontSize: 16.5, color: '#33474d', lineHeight: 1.6 },
  btn: {
    height: 50, borderRadius: 12, border: 'none', width: '100%', cursor: 'pointer', marginTop: 12,
    fontWeight: 800, fontSize: 16, fontFamily: 'inherit', color: '#fff',
    background: 'linear-gradient(135deg,#3C8696,#2C6675)',
  },
  btn2: {
    height: 48, borderRadius: 12, width: '100%', cursor: 'pointer', marginTop: 10,
    fontWeight: 700, fontSize: 15, fontFamily: 'inherit', color: '#2C6675',
    background: 'rgba(60,134,150,.10)', border: '1px solid rgba(60,134,150,.35)',
  },
  ayuda: { margin: '18px 0 0', fontSize: 13.5, color: '#6b7d78', lineHeight: 1.55 },
  det: { marginTop: 14, textAlign: 'left', fontSize: 12, color: '#6b7d78' },
  pre: {
    margin: '6px 0 0', padding: 10, background: '#f2f7f5', border: '1px solid #d8e5e0',
    borderRadius: 8, overflowX: 'auto', fontSize: 11.5, color: '#33474d', whiteSpace: 'pre-wrap',
  },
};

class RedSeguridadGlobal extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Chivatazo para poder ARREGLAR el fallo en vez de adivinarlo (la causa de las
    // pantallas en blanco no se conocía justamente porque nada lo registraba).
    try {
      // eslint-disable-next-line no-console
      console.error('[RedSeguridadGlobal] la pantalla se cayó:', error, info);
    } catch (e) { /* noop */ }
    try {
      const detalle = [
        String(error && (error.message || error)).slice(0, 180),
        (window.location && window.location.pathname) || '',
      ].join(' · ');
      // Fire-and-forget: si no hay sesión o falla, no pasa nada (el aviso ya está).
      import('../utils/track')
        .then((m) => m.track('error_front', detalle))
        .catch(() => {});
    } catch (e) { /* noop */ }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const t = TEXTOS[_idioma()];
    return (
      <div style={S.wrap} role="alert">
        <div style={S.card}>
          <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#2C6675" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 14 }} aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v5" />
            <path d="M12 16h.01" />
          </svg>
          <h1 style={S.titulo}>{t.titulo}</h1>
          <p style={S.texto}>{t.tranquila}</p>
          <p style={S.texto}>{t.consejo}</p>

          <button style={S.btn} onClick={() => this.setState({ error: null })}>{t.reintentar}</button>
          <button style={S.btn2} onClick={() => { try { window.location.reload(); } catch (e) { /* noop */ } }}>
            {t.recargar}
          </button>
          <button style={S.btn2} onClick={() => { try { window.location.href = '/'; } catch (e) { /* noop */ } }}>
            {t.inicio}
          </button>

          <p style={S.ayuda}>{t.ayuda}</p>

          <details style={S.det}>
            <summary style={{ cursor: 'pointer' }}>{t.detalles}</summary>
            <pre style={S.pre}>{String(this.state.error && (this.state.error.message || this.state.error))}</pre>
          </details>
        </div>
      </div>
    );
  }
}

export default RedSeguridadGlobal;
